import { ImmutableObject } from "@hookstate/core";
import { Release } from "../../../../types";
import { useMobileBreakpoint } from "../../../../utils/useBreakpoints";
import { Button, Flex, Text } from "@chakra-ui/react";
import { DownloadIcon } from "../../../../components/icons/reader/DownloadIcon";
import { PrintPagesIcon } from "../../../../components/icons/reader/PrintPagesIcon";
import { DonateIcon } from "../../../../components/icons/reader/DonateIcon";
import React from "react";
import { t } from "i18next";
import { useUserData } from "../../../../hooks/useUserData";
import { FUNNEL_TYPES } from "../../../../components/funnel/utils/contants";
import { FunnelContextArea } from "../../../../components/funnel/types";
import { useGetNavigationContext } from "../../../../hooks/useGetNavigationContext";
import { useBuyPreviewContext } from "../../hooks/useBuyPreviewContext";
import { setFunnelModalConfig } from "../../../../components/funnel/FunnelModal.tsx";
import { ReleasePricingActions } from "../../../../components/cards/ReleasePricingActions";
import { EventContextArea } from "../../../../components/funnel/types";

interface CTAButtonsProps {
  releaseData: ImmutableObject<Release>;
}
export function CTAButtons({ releaseData }: CTAButtonsProps) {
  const { userData } = useUserData();
  const isMobile = useMobileBreakpoint();

  const userOwnsDownload =
    releaseData.release_user_purchase?.is_downloadable === 1;
  const userHasPaidForRelease = releaseData.release_user_purchase;

  const userHasFreePromoCode = !!releaseData.access_token_download;

  const { display_price, is_discounted, display_price_discounted } =
    releaseData.price;

  const pdfNavigationContext = useGetNavigationContext({
    contextArea: EventContextArea.pdfPurchase,
  });
  const openPurchaseFunnel = useBuyPreviewContext(releaseData as Release);

  const releaseIsForPDFSale = !!releaseData?.pdf_download_sale_available;

  return (
    <Flex
      flexDirection="column"
      paddingTop="48px"
      gap="12px"
      flex={1}
      justifyContent={isMobile ? "flex-end" : "flex-start"}
    >
      {openPurchaseFunnel ? (
        <ReleasePricingActions
          release={releaseData as Release}
          setFunnelConfig={openPurchaseFunnel}
          contextArea={FunnelContextArea.readerOverlay}
          eventContextArea={EventContextArea.reader}
          primaryButtonMaxWidth="fit-content"
          buttonSize="md"
          onlyShowALCActions
          hidePreviewAction
          showSaleBadge
          contextualButtonDirection={isMobile ? "top-start" : "bottom-start"}
        />
      ) : null}
      {releaseIsForPDFSale &&
        !userHasFreePromoCode &&
        !userOwnsDownload &&
        !userHasPaidForRelease && (
          <CTAButton
            onClick={() => {
              const context = {
                purchaseRelease: releaseData as Release,
                navigationContext: pdfNavigationContext,
                modalContext: FunnelContextArea.purchasePdf,
              };
              if (userData) {
                setFunnelModalConfig({
                  type: FUNNEL_TYPES.pdf,
                  context,
                });
              } else {
                setFunnelModalConfig({
                  type: FUNNEL_TYPES.signup,
                  context,
                  options: {
                    skipOnboarding: true,
                    onSuccess: () => {
                      setFunnelModalConfig({
                        type: FUNNEL_TYPES.pdf,
                        context,
                      });
                    },
                  },
                });
              }
            }}
            icon={<DownloadIcon />}
            text={`${t("screens.reader.overlay.buyPDF")} (${is_discounted ? display_price_discounted : display_price})`}
            type="secondary"
          />
        )}
      {releaseIsForPDFSale && !userOwnsDownload && userHasFreePromoCode && (
        <CTAButton
          onClick={() => {
            window.open(
              `${releaseData.comic?.url}/buy-release/${releaseData.id}`,
            );
          }}
          icon={<DownloadIcon />}
          text={t("screens.reader.overlay.downloadPDF")}
          type="primary"
        />
      )}
      {userOwnsDownload && (
        <CTAButton
          onClick={() => {
            window.open(`${userData?.edit_url}downloads`);
          }}
          icon={<DownloadIcon />}
          text={t("screens.reader.overlay.downloadPDF")}
          type="primary"
        />
      )}
      {!!releaseData.tracked_website_url && (
        <CTAButton
          onClick={() => window.open(releaseData?.tracked_website_url)}
          icon={<PrintPagesIcon />}
          text={t("screens.reader.overlay.buyPrintEdition")}
          type="primary"
        />
      )}
      {releaseData.is_pod_available &&
        releaseData.pod_price !== undefined &&
        !releaseData.tracked_website_url && (
          <CTAButton
            onClick={() => {
              const context = {
                purchaseRelease: releaseData as Release,
                modalContext: FunnelContextArea.readerOverlay,
              };
              if (userData) {
                setFunnelModalConfig({
                  type: FUNNEL_TYPES.purchasePod,
                  context,
                });
              } else {
                setFunnelModalConfig({
                  type: FUNNEL_TYPES.signup,
                  context,
                  options: {
                    skipOnboarding: true,
                    onSuccess: () => {
                      setFunnelModalConfig({
                        type: FUNNEL_TYPES.purchasePod,
                        context,
                      });
                    },
                  },
                });
              }
            }}
            icon={<PrintPagesIcon />}
            text={`${t("screens.reader.overlay.buyPrintEdition")} ($${releaseData.pod_price})`}
            type="secondary"
          />
        )}
      {!!releaseData?.comic?.artist.accept_donations && (
        <CTAButton
          icon={<DonateIcon />}
          text={t("screens.reader.overlay.donate")}
          type="secondary"
          onClick={() => {
            const context = {
              donationArtistId: releaseData.comic?.artist.id,
              modalContext: FunnelContextArea.donate,
            };
            if (userData) {
              setFunnelModalConfig({
                type: FUNNEL_TYPES.donate,
                context,
              });
            } else {
              setFunnelModalConfig({
                type: FUNNEL_TYPES.signup,
                context,
                options: {
                  skipOnboarding: true,
                  onSuccess: () => {
                    setFunnelModalConfig({
                      type: FUNNEL_TYPES.donate,
                      context,
                    });
                  },
                },
              });
            }
          }}
        />
      )}
    </Flex>
  );
}

interface CTAButtonProps {
  onClick?: () => void;
  text: string;
  icon: JSX.Element;
  type: string;
}
function CTAButton({ onClick, icon, text, type }: CTAButtonProps) {
  const isMobile = useMobileBreakpoint();
  return (
    <Button
      size={isMobile ? "sm" : "md"}
      variant={type}
      onClick={(e) => {
        e.stopPropagation();
        onClick?.();
      }}
    >
      {React.cloneElement(icon, { width: "18px", height: "18px" })}
      <Text
        fontSize={isMobile ? "10px" : "12px"}
        fontWeight="600"
        lineHeight={isMobile ? "10px" : "12px"}
        textTransform="uppercase"
      >
        {text}
      </Text>
    </Button>
  );
}
